import React, { useState, useMemo } from 'react';
import './Component_Calendar.css';
import { Pairing, InterviewPairingsResponse } from '../Requests/interviewPairings';
interface CalendarProps {
  pairingsData: InterviewPairingsResponse;
  onSelectionChange: (dates: Date[]) => void;
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
}

function Component_Calendar({ 
  pairingsData,
  onSelectionChange,
  currentMonth, 
  onMonthChange,
}: CalendarProps) {
  const [selectedMonth, setSelectedMonth] = useState<Date>(currentMonth);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const availableSlots = useMemo(() => {
    if (!pairingsData?.pairings) return [];

    const nowLocal = new Date();
    // const cutoffLocal = new Date(nowLocal.getTime() + 24 * 60 * 60 * 1000);

    const monthStr = String(selectedMonth.getMonth() + 1).padStart(2, '0');
    const year = selectedMonth.getFullYear();
    const ymdSet = new Set<string>();

    (pairingsData.pairings ?? []).forEach((p) => {
      if (!p?.start_time || !p?.slot_available) return;

      // p.start_time is UTC
      const slotUTC = new Date(p.start_time);

      // Convert that UTC instant to LOCAL time for comparison & grouping
      const slotLocal = new Date(slotUTC.getTime()); // same instant, but getHours() etc. are local

      // // If the LOCAL slot time is < 24 hours from LOCAL now, skip
      // if (slotLocal.getTime() < cutoffLocal.getTime()) {
      //   return;
      // }

      // Build local Y-M-D string for the calendar
      const yearLocal = slotLocal.getFullYear();
      const monthLocal = String(slotLocal.getMonth() + 1).padStart(2, '0');
      const dayLocal = String(slotLocal.getDate()).padStart(2, '0');
      const ymdLocal = `${yearLocal}-${monthLocal}-${dayLocal}`;

      // Only keep if in the currently selected month
      if (ymdLocal.startsWith(`${year}-${monthStr}-`)) {
        ymdSet.add(ymdLocal);
      }
    });

    return Array.from(ymdSet);
  }, [pairingsData, selectedMonth]);



  const getDaysInMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date): number => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  const handlePreviousMonth = (): void => {
    const newMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1);
    setSelectedMonth(newMonth);
    onMonthChange(newMonth);
  };

  const handleNextMonth = (): void => {
    const newMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1);
    setSelectedMonth(newMonth);
    onMonthChange(newMonth);
  };

  const handleDateClick = (day: number): void => {
    const date = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const dateString = `${year}-${month}-${dayStr}`;

    // If no valid slot for this date (after 24-hour filter), do nothing
    if (!availableSlots.includes(dateString)) return;

    const alreadySelected = selectedDates.some(
      (d) =>
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate()
    );

    const next = alreadySelected
      ? selectedDates.filter(
          (d) =>
            !(
              d.getFullYear() === date.getFullYear() &&
              d.getMonth() === date.getMonth() &&
              d.getDate() === date.getDate()
            )
        )
      : [...selectedDates, date];

    setSelectedDates(next);
    onSelectionChange(next);
  };



  const isDateAvailable = (day: number): boolean => {
    const year = selectedMonth.getFullYear();
    const month = String(selectedMonth.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const dateKey = `${year}-${month}-${dayStr}`;

    return availableSlots.includes(dateKey);
  };



  const isDateSelected = (day: number): boolean => {
    return selectedDates.some(
      (d) => d.getDate() === day &&
            d.getMonth() === selectedMonth.getMonth() &&
            d.getFullYear() === selectedMonth.getFullYear()
    );
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(selectedMonth);
    const firstDayOfMonth = getFirstDayOfMonth(selectedMonth);
    const days = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <div key={`empty-${i}`} className='calendar-day-cell calendar-day-empty'></div>
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isAvailable = isDateAvailable(day);
      const isSelected = isDateSelected(day);
      
      days.push(
        <div
          key={day}
          className={`calendar-day-cell ${isAvailable ? 'calendar-day-available' : 'calendar-day-unavailable'} ${isSelected ? 'calendar-day-selected' : ''}`}
          onClick={() => handleDateClick(day)}
        >
          <span className='calendar-day-number'>{day}</span>
        </div>
      );
    }

    return days;
  };

  return (
    <div className='calendar-container'>
      <div className='calendar-content'>
        <div className='calendar-content-main'>
          <div className='calendar-header'>
            <button className='calendar-nav-button' onClick={handlePreviousMonth}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className='calendar-month-year'>
              <span className='calendar-month-year-text'>
                {monthNames[selectedMonth.getMonth()]} {selectedMonth.getFullYear()}
              </span>
            </div>
            <button className='calendar-nav-button' onClick={handleNextMonth}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div className='calendar-weekdays'>
            {dayNames.map(day => (
              <div key={day} className='calendar-weekday'>
                <span className='calendar-weekday-text'>{day}</span>
              </div>
            ))}
          </div>
          <div className='calendar-days-grid'>
            {renderCalendarDays()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Component_Calendar;